import PropTypes from "prop-types";

import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";

import "./styles/GallerySubscriptionPopup.css";

const BENEFITS = [
  {
    title: "Upload curated artwork",
    description:
      "Save artwork that reflects your gallery’s identity and get it ready for verification.",
  },
  {
    title: "Keep your profile active",
    description:
      "Your subscription unlocks uploads and keeps your gallery profile discoverable in Suarte.",
  },
  {
    title: "Control your narrative",
    description:
      "Release artwork when you are ready—review and edit drafts before sharing them with collectors.",
  },
  {
    title: "Admin platform",
    description:
      "A single dashboard to update responsibilities, organize exhibitions, manage team members, and more. Faster updates, clearer ownership, less back-and-forth.",
  },
  {
    title: "Sell more, receive more",
    description:
      "Our commission decreases as your sales grow—so you keep more from every sale.",
  },
  {
    title: "Artists can claim their profile",
    description:
      "When you upload works you represent, artists can claim their personal profile—giving them additional exposure across Suarte. You keep full control over how artworks are displayed—artists can’t add pieces; your gallery manages all uploads and edits.",
  },
];

const DEFAULT_HELPER =
  "Start your gallery subscription to unlock uploads. We’ll keep your card on file and charge it monthly for as long as you keep verifying artwork.";

export default function GallerySubscriptionPopup({
  open,
  onClose,
  onConfirm,
  priceHint,
  pricingPromo,
  seatNote,
  error,
  isLoading = false,
}) {
  const helperText = priceHint || DEFAULT_HELPER;

  return (
    <GenericPopup
      open={open}
      className="gallery-subscription-popup__card"
      backgroundClassName="gallery-subscription-popup__backdrop"
    >
      <header className="gallery-subscription-popup__header">
        <p className="gallery-subscription-popup__eyebrow">Verify your gallery</p>
        <h2 className="gallery-subscription-popup__title">
          Upload artworks. Stay visible. Reach further.
        </h2>
        <p className="gallery-subscription-popup__subtitle">
          Your monthly plan enables uploads and keeps your Gallery Profile active, tailored to your gallery’s needs.
        </p>
        {pricingPromo && (
          <div className="gallery-subscription-popup__pricing">
            <span className="gallery-subscription-popup__pricing-label">
              Standard
            </span>
            <span className="gallery-subscription-popup__pricing-standard">
              {pricingPromo.standardLabel}
            </span>
            <span className="gallery-subscription-popup__pricing-label">
              Today
            </span>
            <span className="gallery-subscription-popup__pricing-reduced">
              {pricingPromo.reducedLabel}
            </span>
          </div>
        )}
        {seatNote && (
          <p className="gallery-subscription-popup__seat-note">
            {seatNote}
          </p>
        )}
      </header>
      <ul className="gallery-subscription-popup__benefits">
        {BENEFITS.map((benefit) => (
          <li key={benefit.title} className="gallery-subscription-popup__benefit">
            <h3 className="gallery-subscription-popup__benefit-title">{benefit.title}</h3>
            <p className="gallery-subscription-popup__benefit-description">
              {benefit.description}
            </p>
          </li>
        ))}
      </ul>
      <div className="gallery-subscription-popup__actions">
        {error && (
          <p className="gallery-subscription-popup__error" role="alert">
            {error}
          </p>
        )}
        <p className="gallery-subscription-popup__hint">{helperText}</p>
        <div className="gallery-subscription-popup__button-row">
          <ContinueButton
            onClick={onClose}
            className="generic-popup__close-btn font-size-medium"
            type="button">
            Not now
          </ContinueButton>
          <ContinueButton
            type="button"
            className="gallery-subscription-popup__submit font-size-medium"
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading}>
            Add payment method
          </ContinueButton>
        </div>
      </div>
    </GenericPopup>
  );
}

GallerySubscriptionPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  priceHint: PropTypes.string,
  pricingPromo: PropTypes.shape({
    standardLabel: PropTypes.string,
    reducedLabel: PropTypes.string,
  }),
  seatNote: PropTypes.string,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
};
